import type { DailyTacticalReadiness } from '../../services/operationsApi';
import { FableTone } from './fableLabels';

/** 사용자가 가장 궁금해할 질문을 그대로 묻고 답하는 안전 체크. */
export default function FableSafetyChecklist({ readiness }: Props) {
  const rows = buildRows(readiness);
  return (
    <section className="fable-panel">
      <header className="fable-panel-head">
        <h3>돈과 안전, 바로 확인</h3>
        <p>아래 답이 하나라도 붉게 표시되면 즉시 사용자에게 알려야 하는 상태입니다.</p>
      </header>
      <ul className="fable-check-list">
        {rows.map((row) => (
          <li key={row.internal} className={`fable-check tone-${row.tone}`}>
            <div className="fable-check-q">
              <strong>{row.question}</strong>
            </div>
            <div className="fable-check-a">
              <span>{row.answer}</span>
              <p>{row.note}</p>
            </div>
          </li>
        ))}
      </ul>
      <details className="fable-tech-ref">
        <summary>기술 식별자 보기</summary>
        <p>{rows.map((row) => <code key={row.internal}>{row.question}: {row.internal}</code>)}</p>
      </details>
    </section>
  );
}

interface Row {
  question: string;
  internal: string;
  answer: string;
  note: string;
  tone: FableTone;
}

function buildRows(r: DailyTacticalReadiness): Row[] {
  const s = r.live_order_safety;
  const f = r.flags;
  return [
    {
      question: '실제 돈이 들어가 있나요?',
      internal: 'real capital',
      answer: s.real_capital_allowed ? '허용됨 — 비정상' : '아니요, 0원',
      note: s.real_capital_allowed ? '즉시 확인이 필요합니다.' : '실제 돈 사용은 코드 수준에서 막혀 있습니다.',
      tone: s.real_capital_allowed ? 'danger' : 'safe',
    },
    {
      question: '실제 주문이 나갔나요?',
      internal: 'broker submit',
      answer: s.live_broker_orders_count === 0 ? '아니요, 0건' : `${s.live_broker_orders_count}건 발견 — 비정상`,
      note: s.broker_submit_allowed ? '주문 전송이 허용 상태입니다. 즉시 확인 필요.' : '주문 전송 자체가 잠겨 있습니다.',
      tone: s.live_broker_orders_count === 0 && !s.broker_submit_allowed ? 'safe' : 'danger',
    },
    {
      question: '실전 매매는 잠겨 있나요?',
      internal: 'live lock',
      answer: !s.broker_submit_allowed && !s.real_capital_allowed ? '예, 잠금 유지' : '아니요 — 비정상',
      note: '사용자 승인 없이 이 잠금은 풀리지 않습니다.',
      tone: !s.broker_submit_allowed && !s.real_capital_allowed ? 'safe' : 'danger',
    },
    {
      question: '자동 실행이 켜져 있나요?',
      internal: 'scheduler',
      answer: f.scheduler_enabled ? '예, 켜짐' : '아니요, 꺼짐',
      note: f.scheduler_enabled
        ? '리허설 단계에서는 꺼져 있어야 합니다. 확인이 필요합니다.'
        : '지금 단계에서는 꺼짐이 정상이며, 수동 1회 실행만 검토합니다.',
      tone: f.scheduler_enabled ? 'warn' : 'safe',
    },
    {
      question: '실제 AI 모델이 연결됐나요?',
      internal: 'real model enabled',
      answer: f.real_model_enabled ? '예, 켜짐' : '아니요, 꺼짐',
      note: f.real_model_enabled
        ? '리허설에서 실제 모델 응답과 근거 저장을 검증하는 중입니다.'
        : '공식 관찰 시작 전에는 켜야 하는 항목입니다.',
      tone: f.real_model_enabled ? 'info' : 'warn',
    },
    {
      question: '공식 관찰이 시작됐나요?',
      internal: 'official Stage A clock',
      answer: r.official_stage_clock_started ? '예, 진행 중' : '아니요, 아직 전',
      note: r.official_stage_clock_started
        ? '4-8주 공식 관찰 기간이 흐르고 있습니다.'
        : '리허설 기록은 공식 기간에 포함되지 않습니다.',
      tone: r.official_stage_clock_started ? 'info' : 'safe',
    },
  ];
}

type Props = { readiness: DailyTacticalReadiness };
